/**
 * Phase 4g.E (Feb 2026) — Eingehende Werkstatt-Buchungen Tab + Card extrahiert
 * aus `WorkshopOrdersPage.jsx`. Listet B2C-Anfragen via Booking-Widget und
 * erlaubt dem Händler, sie in einen Werkstatt-Auftrag zu konvertieren.
 *
 * `BookingCard` ist in dieser Datei privat (kein default-Export).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightCircle, CalendarClock, Car, CheckCircle2, Mail, Phone, Wrench, XCircle } from "lucide-react";

import { Button } from "../ui/button";
import PublicBookingLink from "./PublicBookingLink";
import BookingExtrasBadges from "./BookingExtrasBadges";

function BookingsTab({ bookings, onConvert, onUpdateStatus, onRefresh }) {
    const [filter, setFilter] = useState("confirmed"); // standard: nur offene anzeigen
    const counts = {
        confirmed: bookings.filter((b) => b.status === "confirmed").length,
        converted: bookings.filter((b) => b.status === "converted").length,
        completed: bookings.filter((b) => b.status === "completed").length,
        cancelled: bookings.filter((b) => b.status === "cancelled").length,
    };
    const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

    return (
        <div className="space-y-4" data-testid="bookings-tab">
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-3 flex items-start gap-3 text-xs">
                <CalendarClock className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
                <div className="text-blue-900">
                    <strong>Online-Buchungen über Ihre öffentliche Plattform-Buchungsseite.</strong> Mit
                    einem Klick auf <em>„→ Auftrag erstellen&ldquo;</em> wird eine Buchung in einen vollwertigen
                    Werkstatt-Auftrag umgewandelt: Kunde + Fahrzeug werden übernommen, der Service-Type
                    wird als erste Position vorbefüllt, und der Termin landet im Kalender.
                </div>
            </div>

            {/* Jul-2026 (User-Wunsch): Buchungslink-Block in den Werkstatt-Kalender verschoben. */}

            <div className="flex gap-1.5 flex-wrap" data-testid="bookings-filter-chips">
                {[
                    ["confirmed", "Neu / Offen", counts.confirmed, "bg-amber-100 text-amber-900 border-amber-300"],
                    ["converted", "Konvertiert", counts.converted, "bg-blue-100 text-blue-900 border-blue-300"],
                    ["completed", "Erledigt", counts.completed, "bg-emerald-100 text-emerald-900 border-emerald-300"],
                    ["cancelled", "Storniert", counts.cancelled, "bg-gray-200 text-gray-700 border-gray-300"],
                    ["all", "Alle", bookings.length, "bg-white text-gray-800 border-gray-300"],
                ].map(([k, lbl, n, tone]) => (
                    <button
                        key={k}
                        type="button"
                        onClick={() => setFilter(k)}
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${filter === k ? "ring-2 ring-[#FFCC00] " : ""}${tone}`}
                        data-testid={`bookings-filter-${k}`}
                    >
                        {lbl} <span className="text-[10px] font-mono-num bg-white/70 px-1.5 rounded-sm">{n}</span>
                    </button>
                ))}
            </div>

            {filtered.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="bookings-empty">
                    <CalendarClock className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                    <p className="font-bold text-gray-700">
                        {bookings.length === 0 ? "Noch keine Buchungen erhalten" : "Keine Buchungen in dieser Kategorie"}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                        {bookings.length === 0
                            ? "Sobald Kunden über Ihre öffentliche Buchungsseite Termine anfragen, erscheinen sie hier."
                            : "Schalte einen anderen Filter um, um andere Buchungen zu sehen."}
                    </p>
                </div>
            ) : (
                <div className="space-y-2" data-testid="bookings-list">
                    {filtered.map((b) => (
                        <BookingCard key={b.id} booking={b} onConvert={onConvert} onUpdateStatus={onUpdateStatus} onRefresh={onRefresh} />
                    ))}
                </div>
            )}
        </div>
    );
}

function BookingCard({ booking, onConvert, onUpdateStatus, onRefresh }) {
    const dt = booking.date ? new Date(booking.date) : null;
    const statusBadge = {
        confirmed: { label: "Neu / Offen", className: "bg-amber-100 text-amber-900 border-amber-300" },
        converted: { label: "Konvertiert", className: "bg-blue-100 text-blue-900 border-blue-300" },
        completed: { label: "Erledigt", className: "bg-emerald-100 text-emerald-900 border-emerald-300" },
        cancelled: { label: "Storniert", className: "bg-gray-200 text-gray-700 border-gray-300" },
    }[booking.status] || { label: booking.status, className: "bg-gray-100 text-gray-800 border-gray-300" };
    const isOpen = booking.status === "confirmed";

    return (
        <div className={`bg-white border rounded-sm p-4 ${isOpen ? "border-amber-300 shadow-sm" : "border-gray-200"}`} data-testid={`booking-card-${booking.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${statusBadge.className}`}>{statusBadge.label}</span>
                        <span className="inline-flex items-center gap-1 bg-[#FFCC00]/20 text-[#111827] border border-[#FFCC00] px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider">
                            <Wrench className="w-3 h-3" /> {booking.service_type}
                        </span>
                        {booking.linked_work_order_number && (
                            <Link
                                to="#"
                                onClick={(e) => { e.preventDefault(); window.location.hash = `wo-${booking.linked_work_order_id}`; }}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-blue-50 text-blue-900 border border-blue-300 hover:bg-blue-100"
                                data-testid={`booking-linked-${booking.id}`}
                            >
                                → Auftrag {booking.linked_work_order_number}
                            </Link>
                        )}
                    </div>
                    <div className="font-display font-black text-lg">{booking.customer_name}</div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-700 mt-1">
                        <span className="inline-flex items-center gap-1 font-mono-num">
                            <CalendarClock className="w-3 h-3" />
                            {dt ? dt.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "long" }) : booking.date}
                            <strong className="ml-1">{booking.time_slot}</strong>
                            {booking.duration_min && <span className="text-gray-500">· {booking.duration_min} min</span>}
                        </span>
                        {booking.customer_email && (
                            <a href={`mailto:${booking.customer_email}`} className="inline-flex items-center gap-1 hover:underline">
                                <Mail className="w-3 h-3" /> {booking.customer_email}
                            </a>
                        )}
                        {booking.customer_phone && (
                            <a href={`tel:${booking.customer_phone}`} className="inline-flex items-center gap-1 hover:underline">
                                <Phone className="w-3 h-3" /> {booking.customer_phone}
                            </a>
                        )}
                    </div>
                    {(booking.vehicle_brand || booking.vehicle_model) && (
                        <div className="text-xs text-gray-600 mt-1">
                            <Car className="w-3 h-3 inline mr-0.5" /> {[booking.vehicle_brand, booking.vehicle_model].filter(Boolean).join(" ")}
                        </div>
                    )}
                    {booking.notes && (
                        <div className="mt-2 text-[12px] text-gray-700 italic border-l-2 border-gray-300 pl-2" data-testid={`booking-notes-${booking.id}`}>„{booking.notes}&ldquo;</div>
                    )}
                    <BookingExtrasBadges booking={booking} onChanged={onRefresh} />
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                    {isOpen && (
                        <>
                            <Button
                                size="sm"
                                onClick={() => onConvert(booking.id)}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm text-xs uppercase tracking-wider"
                                data-testid={`booking-convert-${booking.id}`}
                            >
                                <ArrowRightCircle className="w-4 h-4 mr-1" /> Auftrag erstellen
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onUpdateStatus(booking.id, "completed")}
                                className="rounded-sm font-bold text-xs uppercase tracking-wider"
                                data-testid={`booking-complete-${booking.id}`}
                            >
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Erledigt
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onUpdateStatus(booking.id, "cancelled")}
                                className="rounded-sm font-bold text-xs uppercase tracking-wider text-red-700 border-red-200 hover:bg-red-50"
                                data-testid={`booking-cancel-${booking.id}`}
                            >
                                <XCircle className="w-3 h-3 mr-1" /> Stornieren
                            </Button>
                        </>
                    )}
                    {booking.status === "converted" && booking.linked_work_order_number && (
                        <div className="text-[10px] text-blue-900 bg-blue-50 border border-blue-200 px-2 py-1 rounded-sm text-center">
                            Im Auftrags-Tab<br />sichtbar
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}





export default BookingsTab;
